import { getUsageRecords } from '@/lib/storage/usage/core';

export interface UsageFrequency {
  id: string;
  count: number;
  lastUsed: Date;
}

// Track selection/usage frequency for different item types
const USAGE_KEY_PREFIX = 'usage_frequency_';

// Get usage frequency for a specific type (e.g., 'category', 'product', 'group')
export const getUsageFrequency = (type: string): UsageFrequency[] => {
  try {
    const stored = localStorage.getItem(`${USAGE_KEY_PREFIX}${type}`);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading usage frequency:', error);
    return [];
  }
};

// Record usage for an item
export const recordUsage = (type: string, itemId: string): void => {
  try {
    const frequencies = getUsageFrequency(type);
    const existingIndex = frequencies.findIndex(f => f.id === itemId);
    
    if (existingIndex >= 0) {
      frequencies[existingIndex].count += 1;
      frequencies[existingIndex].lastUsed = new Date();
    } else {
      frequencies.push({
        id: itemId,
        count: 1,
        lastUsed: new Date()
      });
    }
    
    localStorage.setItem(`${USAGE_KEY_PREFIX}${type}`, JSON.stringify(frequencies));
  } catch (error) {
    console.error('Error recording usage:', error);
  }
};

// Get usage count for a specific product based on actual usage records
export const getProductUsageCount = (productId: string): number => {
  try {
    const usageRecords = getUsageRecords();
    return usageRecords
      .filter(record => record.productId === productId)
      .reduce((total, record) => total + record.quantity, 0);
  } catch (error) {
    console.error('Error getting product usage count:', error);
    return 0;
  }
};

// Get usage count for a specific group based on group session records
export const getGroupUsageCount = (groupId: string, products: Array<{id: string}> = []): number => {
  try {
    const usageRecords = getUsageRecords();
    const groupProductIds = products.map(p => p.id);
    
    // Count unique group sessions for this group
    const groupSessions = new Set<string>();
    let individualUsage = 0;
    
    usageRecords.forEach(record => {
      if (groupProductIds.includes(record.productId)) {
        if (record.groupSessionId) {
          groupSessions.add(record.groupSessionId);
        } else {
          individualUsage++;
        }
      }
    });
    
    return groupSessions.size + individualUsage;
  } catch (error) {
    console.error('Error getting group usage count:', error);
    return 0;
  }
};

// Sort items by usage frequency, then alphabetically
export const sortByUsageAndAlphabet = <T extends { id: string, name: string }>(
  items: T[],
  type: string,
  getUsageCount?: (item: T) => number
): T[] => {
  const frequencies = getUsageFrequency(type);
  const frequencyMap = new Map(frequencies.map(f => [f.id, f.count]));
  
  return [...items].sort((a, b) => {
    // Get usage counts
    const aUsage = getUsageCount ? getUsageCount(a) : (frequencyMap.get(a.id) || 0);
    const bUsage = getUsageCount ? getUsageCount(b) : (frequencyMap.get(b.id) || 0);
    
    // Sort by usage count first (descending)
    if (aUsage !== bUsage) {
      return bUsage - aUsage;
    }
    
    // Then by name alphabetically (ascending)
    return a.name.localeCompare(b.name);
  });
};

// Sort categories by usage frequency, then alphabetically
export const sortCategoriesByUsage = (categories: string[]): string[] => {
  const frequencies = getUsageFrequency('category');
  const frequencyMap = new Map(frequencies.map(f => [f.id, f.count]));
  
  return [...categories].sort((a, b) => {
    const aUsage = frequencyMap.get(a) || 0;
    const bUsage = frequencyMap.get(b) || 0;
    
    // Sort by usage count first (descending)
    if (aUsage !== bUsage) {
      return bUsage - aUsage;
    }
    
    // Then by name alphabetically (ascending)
    return a.localeCompare(b);
  });
};